import React, { useContext, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useRemoteData } from 'lib/RemoteData'
import { API_HOST } from 'lib'
import { Dialog } from '../../components/Dialog'
import { authenticatedFetch, authenticatedPost, extractErrorMessage } from 'lib/authenticatedGet'
import { Container, InnerContainer, Label } from 'styles/PageLayout'
import { Link } from 'react-router'
import { FileUploadResultDialog } from './FileUploadResultDialog'
import { FileStreamDialog } from 'components/FileStreamDialog'
import { TitleWithEdit } from 'components/TitleWithEdit'
import { FormLayout } from 'styles/FormLayout'
import styled from 'styled-components'
import { FileHistory } from 'components/FileHistory'
import { CommonReferences } from 'components/CommonReferences'
import { FileStreamWithHistory, FileUploadResult } from 'domain/DTO'
import { SelectedCountryContext } from 'domain/SelectedCountryContext'
import { SRD } from 'srd'
import { ArrowRightIcon } from '../../icons'
import HomeIcon from '../../icons/HomeIcon'
import { hasViewAssortmentPermission } from '../../lib/Permission'
import { LoadingSpinner } from '../../components/LoadingSpinner'

const FileHeader = styled.div`
  display: flex;
  flex-direction: column;
`

const FileHeaderRow = styled.div`
  display: flex;
`

const FileHeaderLabel = styled.div`
  flex: 1 0 5rem;
  min-height: 1.5rem;
  font-weight: 'bold';
`

const FileHeaderValue = styled.div`
  flex: 1 0 15rem;
  font-weight: 'bold';
`

const postFileAsMultipart = async (file: File, streamId: string) => {
  const form = new FormData()
  form.append('file', file)
  return authenticatedPost(
    `${API_HOST}/v1/filestream/stage/${streamId}`,
    {
      body: form,
    },
    1200000,
  )
}

type Tabs = 'HISTORY' | 'COMMONS'

export const FileStream = () => {
  const { id } = useParams<{ id: string }>()
  if (!id) {
    throw new Error('Missing id parameter')
  }
  const { data: fileStreamRemoteData, refresh: refreshFileStream } = useRemoteData<FileStreamWithHistory>(
    `${API_HOST}/v1/filestream/${id}`,
  )
  const fileInput = useRef<HTMLInputElement>(null)
  const [fileSelected, setFileSelected] = useState(false)
  const [fileUploadResult, setFileUploadResult] = useState<FileUploadResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [activeTab, setActiveTab] = useState<Tabs>('COMMONS')
  const { selectedCountry } = useContext(SelectedCountryContext)

  const handleFileChanged = () => {
    const hasFile = (fileInput.current?.files?.length ?? 0) > 0
    setFileSelected(hasFile)
  }

  return (
    hasViewAssortmentPermission() &&
    SRD.match(
      {
        notAsked: () => <LoadingSpinner />,
        loading: () => <LoadingSpinner />,
        failure: (err) => (
          <div className="card">
            <div className="centerAbsolute">
              <div className="alert alert-danger">
                Uh-oh.. Failed to load File Stream Details
                <div>{err.body?.title}</div>
              </div>
            </div>
          </div>
        ),
        success: (value) => {
          const isUploadButtonDisabled = isUploading || !fileSelected
          return (
            <div>
              <div className="py-md px-lg flex items-center space-x-md">
                <Link aria-label="Home" to={'/'}>
                  <HomeIcon size="1.5rem" />
                </Link>
                <ArrowRightIcon size="1rem" />
                <Link to="/file-stream">
                  <h2>Files</h2>
                </Link>
                <ArrowRightIcon size="1rem" />
                <h2>{value.name}</h2>
              </div>

              <div className="card">
                <Container>
                  <TitleWithEdit title={value.name} onClick={() => setShowEditDialog(true)} />
                  <FileHeader>
                    <FileHeaderRow>
                      <FileHeaderLabel>Type: </FileHeaderLabel>
                      <FileHeaderValue>{value.fileType}</FileHeaderValue>
                    </FileHeaderRow>
                    <FileHeaderRow>
                      <FileHeaderLabel>Name: </FileHeaderLabel>
                      <FileHeaderValue>
                        {value.current ? (
                          value.current.url ? (
                            <Link className="link" to={value.current.url}>
                              {value.current.fileName}
                            </Link>
                          ) : (
                            value.current.fileName
                          )
                        ) : (
                          'No file uploaded yet..'
                        )}
                      </FileHeaderValue>
                    </FileHeaderRow>
                    <FileHeaderRow>
                      <FileHeaderLabel>Version: </FileHeaderLabel>
                      <FileHeaderValue>{value.current ? value.current.parsedVersion : ''}</FileHeaderValue>
                    </FileHeaderRow>
                    <FileHeaderRow>
                      <FileHeaderLabel>Uploaded: </FileHeaderLabel>
                      <FileHeaderValue>{value.current?.created?.toLocaleString() ?? ''}</FileHeaderValue>
                    </FileHeaderRow>
                  </FileHeader>
                  <InnerContainer
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 50 }}
                  >
                    <Label>
                      <br />
                      <input type="file" ref={fileInput} onChange={handleFileChanged} />
                    </Label>
                    <button
                      className="btn btn-primary-dark py-sm"
                      disabled={isUploadButtonDisabled}
                      title={isUploadButtonDisabled ? 'You must choose file before you can upload' : 'Upload file'}
                      style={{ marginTop: 20 }}
                      onClick={async (e) => {
                        e.preventDefault()
                        setIsUploading(true)
                        if (fileInput.current && fileInput.current.files) {
                          try {
                            const responseBody = await postFileAsMultipart(fileInput.current.files[0], id)
                            // @ts-expect-error responsebody is unknown
                            setFileUploadResult(responseBody.data as FileUploadResult)
                          } catch (err) {
                            setFileUploadResult({
                              stagedFile: null,
                              diff: null,
                              messages: [{ entityId: null, severity: 'ERROR', message: err?.toString() }],
                            })
                          }
                          setIsUploading(false)
                        }
                      }}
                    >
                      <FormLayout.LoadingButtonText
                        loading={isUploading}
                        text="Upload File"
                        loadingText="Uploading File..."
                      />
                    </button>
                  </InnerContainer>
                  <div>
                    <div className="tabs w-6/12">
                      <button
                        className={`tab ${activeTab === 'COMMONS' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('COMMONS')
                        }}
                      >
                        Used by
                      </button>
                      <button
                        className={`tab ${activeTab === 'HISTORY' ? 'active' : ''}`}
                        onClick={() => {
                          setActiveTab('HISTORY')
                        }}
                      >
                        File history
                      </button>
                    </div>
                    {activeTab === 'COMMONS' && <CommonReferences commons={value.commonAssortmentReferences} />}
                    {activeTab === 'HISTORY' && <FileHistory history={value.history} />}
                  </div>
                  {fileUploadResult ? (
                    <Dialog
                      onClose={() => setFileUploadResult(null)}
                      shouldCloseOnOverlayClick={false}
                      contentClassName={'w-8/12'}
                    >
                      <div className="card p-lg">
                        <FileUploadResultDialog
                          result={fileUploadResult}
                          latestFileVersion={value.current ? value.current.parsedVersion : null}
                          onApply={() => {
                            return authenticatedFetch(`${API_HOST}/v1/filestream/apply/${id}`, {
                              method: 'POST',
                              body: JSON.stringify({ data: fileUploadResult.stagedFile }),
                            }).then(() => {
                              setFileUploadResult(null)
                              refreshFileStream()
                            })
                          }}
                        />
                      </div>
                    </Dialog>
                  ) : null}
                  {showEditDialog ? (
                    <Dialog shouldCloseOnOverlayClick={false} onClose={() => setShowEditDialog(false)}>
                      <FileStreamDialog
                        editing={{ name: value.name, type: value.fileType }}
                        onComplete={async (name, type) => {
                          updateFileStream(value.streamId, name, selectedCountry, type).finally(() => {
                            setShowEditDialog(false)
                            refreshFileStream()
                          })
                        }}
                      />
                    </Dialog>
                  ) : null}
                </Container>
              </div>
            </div>
          )
        },
      },
      fileStreamRemoteData,
    )
  )
}

const updateFileStream = async (id: string, streamName: string, country: string, type: string) => {
  return authenticatedFetch(`${API_HOST}/v1/filestream/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      data: {
        streamName,
        country,
        type,
      },
    }),
  }).catch(extractErrorMessage)
}
